// src/private/src/context/dashboard/DailyPulse.jsx
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchDailyPulseByClient,
  fetchDailyPulseByCampaign,
  fetchDailyPulseByChannel,
  fetchDailyPulseByVertical,
  fetchBudgetPacing,
} from "@src/api";
import { useAuth } from "@src/context";
import { DEFAULT_CLIENT_STATUS_FILTER } from "@src/constants/clients";
import { formatDateToYMD, getCurrentMonthDates } from "@src/utils/dateUtils";

const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const numberOrZero = (value) => toFiniteNumber(value) ?? 0;

const roundToTwo = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return 0;
  }
  return Math.round(numeric * 100) / 100;
};

const clampToZero = (value) => {
  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return 0;
  }
  return numeric < 0 ? 0 : numeric;
};

const deriveDaysRemaining = (budgetRemaining, remainingPerDay) => {
  const remaining = toFiniteNumber(budgetRemaining);
  const perDay = toFiniteNumber(remainingPerDay);
  if (remaining === null || perDay === null || perDay <= 0) {
    return null;
  }
  const value = remaining / perDay;
  return Number.isFinite(value) ? value : null;
};

const extractPacingMeta = (metrics) => {
  if (!metrics || typeof metrics !== 'object') {
    return {};
  }

  const meta = metrics.__pacing_metadata || metrics.pacing_metadata;
  if (meta && typeof meta === 'object') {
    return meta;
  }

  return {};
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const buildTrailingRange = (days, referenceDate = new Date()) => {
  const anchor = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  if (!(anchor instanceof Date) || Number.isNaN(anchor.getTime())) {
    return null;
  }

  const end = new Date(anchor);
  end.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() - 1);
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));

  return {
    date_after: formatDateToYMD(start),
    date_before: formatDateToYMD(end),
  };
};

const normalizeVerticalName = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const getClientLookupKey = (row = {}) => {
  if (row.client_id) {
    return `client-${row.client_id}`;
  }
  return row.uuid || null;
};

const getChannelLookupKey = (row = {}) => {
  if (row.client_id && row.channel_id) {
    return `client-${row.client_id}-channel-${row.channel_id}`;
  }
  return row.uuid || null;
};

const getVerticalLookupKey = (row = {}) => {
  const name = normalizeVerticalName(row.vertical);
  if (!name || !row.client_id) {
    return row.client_id ? `client-${row.client_id}-vertical-unknown` : row.uuid || null;
  }
  const channelKey = row.channel_id ? `channel-${row.channel_id}` : 'all';
  return `client-${row.client_id}-${channelKey}-vertical-${name}`;
};

const getCampaignLookupKey = (row = {}) => {
  const campaignIdentifier = row.campaign || row.campaign_name || row.campaign_id;
  if (!row.client_id || !row.channel_id || !campaignIdentifier) {
    return row.uuid || null;
  }
  return `client-${row.client_id}-channel-${row.channel_id}-campaign-${String(
    campaignIdentifier
  )
    .trim()
    .toLowerCase()}`;
};

const buildCostOfMarketingLookup = (rows, buildKey) => {
  const map = new Map();
  if (!Array.isArray(rows)) {
    return map;
  }
  rows.forEach((row = {}) => {
    const key = buildKey(row);
    if (!key) {
      return;
    }
    const metrics = withMarketingMetrics(row);
    map.set(key, metrics.costOfMarketing ?? null);
  });
  return map;
};

const buildRollingLookups = (last30Rows, last60Rows, keyFn) => ({
  last30: buildCostOfMarketingLookup(last30Rows, keyFn),
  last60: buildCostOfMarketingLookup(last60Rows, keyFn),
});

const attachRollingCosts = (row, buildKey, rollingLookups = {}) => {
  if (!row) {
    return row;
  }
  const key = buildKey(row);
  const last30Lookup = rollingLookups.last30 ?? new Map();
  const last60Lookup = rollingLookups.last60 ?? new Map();

  return {
    ...row,
    costOfMarketingLast30: key ? last30Lookup.get(key) ?? null : null,
    costOfMarketingLast60: key ? last60Lookup.get(key) ?? null : null,
  };
};

const getDaysLeftInMonthIncludingToday = (inputDate) => {
  const reference =
    inputDate instanceof Date
      ? inputDate
      : inputDate
        ? new Date(inputDate)
        : new Date();
  if (!(reference instanceof Date) || Number.isNaN(reference.getTime())) {
    return null;
  }

  const year = reference.getFullYear();
  const month = reference.getMonth();
  const startOfTodayUTC = Date.UTC(year, month, reference.getDate());
  const startOfNextMonthUTC = Date.UTC(year, month + 1, 1);
  const diff = Math.floor((startOfNextMonthUTC - startOfTodayUTC) / MS_PER_DAY);

  if (!Number.isFinite(diff)) {
    return null;
  }

  return diff > 0 ? diff : 0;
};

const recalculateBudgetMetrics = (row, metrics, merged, options = {}) => {
  if (!row || !metrics) {
    return merged;
  }

  const { shouldFallbackToActuals = false, calendarToday } = options;

  const budgetAmount = toFiniteNumber(
    metrics.budget_amount ?? metrics.budget ?? merged.budget_amount ?? merged.budget
  );

  if (budgetAmount === null) {
    return merged;
  }

  const feeValue = toFiniteNumber(row.client_fee ?? merged.client_fee);
  const fee = feeValue ?? 0;
  const feeMultiplier = 1 + fee;

  const baseMeta = extractPacingMeta(metrics);
  const metaMonthThroughYesterdayWithFees = toFiniteNumber(
    metrics.month_through_yesterday_with_fees ??
      baseMeta.month_through_yesterday_with_fees ??
      baseMeta.actual_through_yesterday_with_fees
  );
  const metaActualMonthToDateWithFees = toFiniteNumber(
    metrics.actual_month_to_date_with_fees ??
      baseMeta.actual_month_to_date_with_fees ??
      baseMeta.spent_with_fees
  );
  const metaPacingMonthToDateWithFees = toFiniteNumber(
    metrics.pacing_month_to_date_with_fees ??
      metrics.month_to_date_with_fees ??
      baseMeta.pacing_month_to_date_with_fees ??
      baseMeta.month_to_date_with_fees ??
      baseMeta.month_through_yesterday_with_fees
  );
  const metaTodaySpendWithFees = toFiniteNumber(
    metrics.today_spend_with_fees ?? baseMeta.today_spend_with_fees
  );

  const parsedYesterdayWithFees = toFiniteNumber(
    baseMeta.yesterday_spend_with_fees ??
      metrics.yesterday_spend_with_fees ??
      merged.yesterday_spend_with_fees
  );
  const parsedYesterday = toFiniteNumber(
    baseMeta.yesterday_spend ?? metrics.yesterday_spend ?? merged.yesterday_spend
  );

  const hasWithFees = typeof parsedYesterdayWithFees === 'number';
  const hasRawYesterday = typeof parsedYesterday === 'number';

  const yesterdaySpendRaw = hasRawYesterday ? clampToZero(parsedYesterday) : 0;
  const yesterdaySpendWithFees = hasWithFees
    ? clampToZero(parsedYesterdayWithFees)
    : yesterdaySpendRaw * feeMultiplier;
  const hasValidYesterday = hasWithFees || hasRawYesterday;

  let daysRemaining = baseMeta.days_remaining;
  if (!(typeof daysRemaining === 'number' && Number.isFinite(daysRemaining))) {
    daysRemaining = deriveDaysRemaining(
      metrics.budget_remaining ?? merged.budget_remaining,
      metrics.remaining_per_day ?? merged.remaining_per_day
    );
  }

  const hasValidDaysRemaining =
    typeof daysRemaining === 'number' && Number.isFinite(daysRemaining);
  const safeDaysRemaining = hasValidDaysRemaining ? Math.max(daysRemaining, 0) : 0;
  const forecastDays = hasValidDaysRemaining ? safeDaysRemaining : 0;

  const calendarDaysRemainingRaw = getDaysLeftInMonthIncludingToday(calendarToday);
  const hasCalendarDaysRemaining =
    typeof calendarDaysRemainingRaw === 'number' && Number.isFinite(calendarDaysRemainingRaw);
  const calendarDaysRemaining = hasCalendarDaysRemaining
    ? Math.max(calendarDaysRemainingRaw, 0)
    : null;

  const fallbackActualRaw = toFiniteNumber(
    row.total_spend_sum ?? merged.total_spend_sum
  );
  const fallbackActual =
    typeof fallbackActualRaw === 'number' && Number.isFinite(fallbackActualRaw)
      ? fallbackActualRaw
      : null;

  const pacingMonthToDateCandidate = toFiniteNumber(
    metrics.pacing_month_to_date_spend ??
      metrics.month_to_date_spend ??
      baseMeta.pacing_month_to_date_spend ??
      baseMeta.month_to_date_spend
  );
  let resolvedPacing = pacingMonthToDateCandidate;
  if (
    resolvedPacing === null &&
    typeof metaPacingMonthToDateWithFees === 'number' &&
    Number.isFinite(metaPacingMonthToDateWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derived = metaPacingMonthToDateWithFees / divisor;
      if (Number.isFinite(derived)) {
        resolvedPacing = derived;
      }
    }
  }

  let todaySpendCandidate = toFiniteNumber(
    metrics.today_spend ?? baseMeta.today_spend
  );

  if (
    todaySpendCandidate === null &&
    typeof metaTodaySpendWithFees === 'number' &&
    Number.isFinite(metaTodaySpendWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedToday = metaTodaySpendWithFees / divisor;
      if (Number.isFinite(derivedToday)) {
        todaySpendCandidate = derivedToday;
      }
    }
  }

  if (shouldFallbackToActuals && fallbackActual !== null) {
    if (todaySpendCandidate === null && typeof resolvedPacing === 'number') {
      const derivedToday = fallbackActual - resolvedPacing;
      if (Number.isFinite(derivedToday)) {
        todaySpendCandidate = derivedToday;
      }
    }

    if (todaySpendCandidate === null && resolvedPacing === null) {
      todaySpendCandidate = fallbackActual;
    }

    if (resolvedPacing === null && typeof todaySpendCandidate === 'number') {
      const derivedPacing = fallbackActual - todaySpendCandidate;
      if (Number.isFinite(derivedPacing)) {
        resolvedPacing = derivedPacing;
      }
    }
  }

  const hasResolvedPacing =
    typeof resolvedPacing === 'number' && Number.isFinite(resolvedPacing);
  const hasTodaySpend =
    typeof todaySpendCandidate === 'number' && Number.isFinite(todaySpendCandidate);

  const pacingMonthToDate = clampToZero(hasResolvedPacing ? resolvedPacing : 0);
  const todaySpend = clampToZero(hasTodaySpend ? todaySpendCandidate : 0);

  const hasTodaySpendWithFeesMeta =
    typeof metaTodaySpendWithFees === 'number' && Number.isFinite(metaTodaySpendWithFees);
  const hasPacingWithFeesMeta =
    typeof metaPacingMonthToDateWithFees === 'number' &&
    Number.isFinite(metaPacingMonthToDateWithFees);
  const hasMonthThroughYesterdayWithFeesMeta =
    typeof metaMonthThroughYesterdayWithFees === 'number' &&
    Number.isFinite(metaMonthThroughYesterdayWithFees);
  const todaySpendWithFees = hasTodaySpendWithFeesMeta
    ? clampToZero(metaTodaySpendWithFees)
    : todaySpend * feeMultiplier;

  let actualMonthToDate = pacingMonthToDate + todaySpend;
  if (shouldFallbackToActuals && fallbackActual !== null) {
    actualMonthToDate = Math.max(actualMonthToDate, fallbackActual, 0);
  }

  if (
    typeof metaActualMonthToDateWithFees === 'number' &&
    Number.isFinite(metaActualMonthToDateWithFees)
  ) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedActual = metaActualMonthToDateWithFees / divisor;
      if (Number.isFinite(derivedActual)) {
        actualMonthToDate = Math.max(actualMonthToDate, derivedActual, 0);
      }
    }
  }

  const spentWithFees =
    typeof metaActualMonthToDateWithFees === 'number' &&
    Number.isFinite(metaActualMonthToDateWithFees)
      ? clampToZero(metaActualMonthToDateWithFees)
      : actualMonthToDate * feeMultiplier;
  const pacingMonthToDateWithFees = hasPacingWithFeesMeta
    ? clampToZero(metaPacingMonthToDateWithFees)
    : pacingMonthToDate * feeMultiplier;

  let actualThroughYesterday = Math.max(
    actualMonthToDate - todaySpend,
    pacingMonthToDate,
    0
  );
  if (hasMonthThroughYesterdayWithFeesMeta) {
    const divisor = feeMultiplier || 1;
    if (divisor !== 0) {
      const derivedThrough = metaMonthThroughYesterdayWithFees / divisor;
      if (Number.isFinite(derivedThrough)) {
        actualThroughYesterday = Math.max(actualThroughYesterday, derivedThrough, 0);
      }
    }
  }
  const computedMonthThroughYesterdayWithFees = Math.max(
    spentWithFees - todaySpendWithFees,
    pacingMonthToDateWithFees,
    actualThroughYesterday * feeMultiplier,
    0
  );
  const monthThroughYesterdayWithFees = hasMonthThroughYesterdayWithFeesMeta
    ? clampToZero(metaMonthThroughYesterdayWithFees)
    : clampToZero(computedMonthThroughYesterdayWithFees);
  const actualThroughYesterdayWithFees = monthThroughYesterdayWithFees;

  merged.month_to_date_spend = roundToTwo(spentWithFees);
  merged.budget_amount = roundToTwo(budgetAmount);

  const budgetRemaining = clampToZero(budgetAmount - spentWithFees);

  const projectionDaysRaw =
    calendarDaysRemaining !== null ? calendarDaysRemaining : forecastDays;
  const hasProjectionDays =
    typeof projectionDaysRaw === 'number' && Number.isFinite(projectionDaysRaw);
  const projectionDays = hasProjectionDays ? Math.max(projectionDaysRaw, 0) : null;

  const projectedCandidate =
    projectionDays !== null
      ? monthThroughYesterdayWithFees + yesterdaySpendWithFees * projectionDays
      : monthThroughYesterdayWithFees;

  let projected = Number.isFinite(projectedCandidate)
    ? projectedCandidate
    : monthThroughYesterdayWithFees;
  projected = Math.max(projected, spentWithFees, monthThroughYesterdayWithFees);
  projected = clampToZero(projected);

  const remainingPerDay =
    forecastDays > 0 ? clampToZero(budgetRemaining / forecastDays) : 0;
  const pacingPercentage =
    budgetAmount > 0 ? clampToZero((projected / budgetAmount) * 100) : 0;

  const calendarBudgetRemainingPerDay =
    typeof calendarDaysRemaining === 'number' &&
    Number.isFinite(calendarDaysRemaining) &&
    calendarDaysRemaining > 0
      ? budgetRemaining / (feeMultiplier || 1) / calendarDaysRemaining
      : null;

  merged.budget_remaining = roundToTwo(budgetRemaining);
  merged.projected_spend = roundToTwo(projected);
  merged.remaining_per_day = roundToTwo(remainingPerDay);
  merged.pacing_percentage = roundToTwo(pacingPercentage);
  if (
    typeof calendarBudgetRemainingPerDay === 'number' &&
    Number.isFinite(calendarBudgetRemainingPerDay)
  ) {
    merged.calendar_budget_remaining_per_day = roundToTwo(
      calendarBudgetRemainingPerDay
    );
  } else {
    delete merged.calendar_budget_remaining_per_day;
  }
  if (hasValidYesterday) {
    merged.yesterday_spend = roundToTwo(yesterdaySpendWithFees);
    merged.yesterday_spend_with_fees = roundToTwo(yesterdaySpendWithFees);
    if (hasRawYesterday) {
      merged.yesterday_spend_without_fees = roundToTwo(yesterdaySpendRaw);
    } else {
      delete merged.yesterday_spend_without_fees;
    }
  } else {
    delete merged.yesterday_spend_with_fees;
    delete merged.yesterday_spend_without_fees;
  }

  const nextMeta = {
    ...baseMeta,
    spent_with_fees: spentWithFees,
    actual_month_to_date_with_fees: spentWithFees,
    actual_month_to_date_spend: actualMonthToDate,
    actual_through_yesterday_spend: actualThroughYesterday,
    actual_through_yesterday_with_fees: actualThroughYesterdayWithFees,
    month_through_yesterday_with_fees: monthThroughYesterdayWithFees,
  };

  if (hasValidDaysRemaining) {
    nextMeta.days_remaining = safeDaysRemaining;
    nextMeta.safe_days_remaining = safeDaysRemaining;
    nextMeta.forecast_days = forecastDays;
  } else {
    delete nextMeta.days_remaining;
    delete nextMeta.safe_days_remaining;
    delete nextMeta.forecast_days;
  }

  if (hasValidYesterday) {
    nextMeta.yesterday_spend = yesterdaySpendWithFees;
    nextMeta.yesterday_spend_with_fees = yesterdaySpendWithFees;
    if (hasRawYesterday) {
      nextMeta.yesterday_spend_without_fees = yesterdaySpendRaw;
    } else {
      delete nextMeta.yesterday_spend_without_fees;
    }
  } else {
    delete nextMeta.yesterday_spend;
    delete nextMeta.yesterday_spend_with_fees;
    delete nextMeta.yesterday_spend_without_fees;
  }

  if (
    hasResolvedPacing ||
    hasPacingWithFeesMeta ||
    (shouldFallbackToActuals && fallbackActual !== null)
  ) {
    nextMeta.pacing_month_to_date_spend = pacingMonthToDate;
    nextMeta.month_to_date_spend = pacingMonthToDate;
    nextMeta.pacing_month_to_date_with_fees = pacingMonthToDateWithFees;
  } else {
    delete nextMeta.pacing_month_to_date_spend;
    delete nextMeta.month_to_date_spend;
    delete nextMeta.pacing_month_to_date_with_fees;
  }

  if (
    hasTodaySpend ||
    hasTodaySpendWithFeesMeta ||
    (shouldFallbackToActuals && fallbackActual !== null)
  ) {
    nextMeta.today_spend = todaySpend;
    nextMeta.today_spend_with_fees = todaySpendWithFees;
  } else {
    delete nextMeta.today_spend;
    delete nextMeta.today_spend_with_fees;
  }

  if (
    typeof calendarDaysRemaining === 'number' &&
    Number.isFinite(calendarDaysRemaining)
  ) {
    nextMeta.calendar_days_remaining = calendarDaysRemaining;
  } else {
    delete nextMeta.calendar_days_remaining;
  }

  if (
    typeof calendarBudgetRemainingPerDay === 'number' &&
    Number.isFinite(calendarBudgetRemainingPerDay)
  ) {
    nextMeta.calendar_budget_remaining_per_day = calendarBudgetRemainingPerDay;
  } else {
    delete nextMeta.calendar_budget_remaining_per_day;
  }

  Object.keys(nextMeta).forEach((key) => {
    const value = nextMeta[key];
    if (value === undefined || value === null) {
      delete nextMeta[key];
    } else if (typeof value === 'number' && !Number.isFinite(value)) {
      delete nextMeta[key];
    }
  });

  if (Object.keys(nextMeta).length > 0) {
    merged.__pacing_metadata = nextMeta;
  } else {
    delete merged.__pacing_metadata;
  }

  const inheritedMeta = merged.__pacing_metadata;
  if (inheritedMeta && Array.isArray(merged.vertical_budget_breakdown)) {
    merged.vertical_budget_breakdown = merged.vertical_budget_breakdown.map((item) => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      const existingMeta = extractPacingMeta(item);
      const combinedMeta =
        existingMeta && typeof existingMeta === 'object' ? { ...existingMeta } : {};

      let shouldAttachMeta = false;
      const assignMetaValue = (key) => {
        const value = inheritedMeta[key];
        if (value === undefined) {
          return;
        }
        if (combinedMeta[key] !== value) {
          combinedMeta[key] = value;
        }
        shouldAttachMeta = true;
      };

      assignMetaValue('forecast_days');
      assignMetaValue('calendar_days_remaining');
      assignMetaValue('calendar_budget_remaining_per_day');
      assignMetaValue('month_through_yesterday_with_fees');
      assignMetaValue('actual_through_yesterday_with_fees');
      assignMetaValue('actual_through_yesterday_spend');
      assignMetaValue('pacing_month_to_date_with_fees');
      assignMetaValue('actual_month_to_date_with_fees');
      assignMetaValue('today_spend_with_fees');
      assignMetaValue('spent_with_fees');

      if (!shouldAttachMeta) {
        return item;
      }

      return {
        ...item,
        __pacing_metadata: combinedMeta,
      };
    });
  }

  return merged;
};

const withMarketingMetrics = (row) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const clientFee = toFiniteNumber(row.client_fee) ?? 0;
  const spend = numberOrZero(row.total_spend_sum);
  const netAmount = toFiniteNumber(row.net_amount_sum);
  const existingGoalRatio =
    typeof row.costOfMarketingGoal === 'number' ? row.costOfMarketingGoal : null;
  const rawGoalPercent = toFiniteNumber(row.cost_of_marketing_goal);
  const costOfMarketingGoal =
    existingGoalRatio ?? (typeof rawGoalPercent === 'number' ? rawGoalPercent / 100 : null);

  const spendPlusFees = spend * (1 + clientFee);
  const hasNetRevenue = typeof netAmount === 'number' && netAmount !== 0;
  const costOfMarketing = hasNetRevenue ? spendPlusFees / netAmount : null;

  return {
    ...row,
    client_fee: clientFee,
    spendPlusFees,
    costOfMarketing,
    costOfMarketingGoal,
  };
};

const DailyPulseContext = React.createContext(null);
export const useDailyPulse = () => useContext(DailyPulseContext);

export const DailyPulseProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState([]);
  const [tree, setTree] = useState([]);
  const { isAuthenticated } = useAuth();

  const [monthStart, monthEnd] = useMemo(() => getCurrentMonthDates(), []);
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(monthEnd);
  const [orderBy, setOrderBy] = useState("total_spend_sum");
  const [orderDirection, setOrderDirection] = useState("desc");
  const [clientStatusFilter, setClientStatusFilter] = useState(DEFAULT_CLIENT_STATUS_FILTER);
  const [clientTierFilter, setClientTierFilter] = useState([]);
  const [pacingLookup, setPacingLookup] = useState({ clients: {}, channels: {} });

  const monthAnchorDate = useMemo(() => {
    const source = monthStart instanceof Date ? monthStart : new Date(monthStart);
    if (!(source instanceof Date) || Number.isNaN(source.getTime())) {
      return null;
    }
    return source;
  }, [monthStart]);

  const calendarToday = useMemo(() => {
    const now = new Date();
    const hasValidNow = now instanceof Date && !Number.isNaN(now.getTime());

    const resolvedMonthEnd =
      monthEnd instanceof Date ? monthEnd : monthEnd ? new Date(monthEnd) : null;
    const hasValidMonthEnd =
      resolvedMonthEnd instanceof Date && !Number.isNaN(resolvedMonthEnd?.getTime?.());

    if (hasValidNow && monthAnchorDate) {
      const sameMonth =
        now.getUTCFullYear() === monthAnchorDate.getUTCFullYear() &&
        now.getUTCMonth() === monthAnchorDate.getUTCMonth();

      if (sameMonth) {
        return now;
      }
    }

    if (hasValidMonthEnd) {
      return resolvedMonthEnd;
    }

    return hasValidNow ? now : null;
  }, [monthEnd, monthAnchorDate]);

  const isCurrentMonthRange = useMemo(() => {
    if (!monthAnchorDate) {
      return false;
    }
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    if (!(start instanceof Date) || Number.isNaN(start.getTime())) {
      return false;
    }
    if (!(end instanceof Date) || Number.isNaN(end.getTime())) {
      return false;
    }
    return (
      start.getUTCFullYear() === monthAnchorDate.getUTCFullYear() &&
      start.getUTCMonth() === monthAnchorDate.getUTCMonth() &&
      end.getUTCFullYear() === monthAnchorDate.getUTCFullYear() &&
      end.getUTCMonth() === monthAnchorDate.getUTCMonth()
    );
  }, [startDate, endDate, monthAnchorDate]);

  const baseFilters = useMemo(() => {
    const filters = {
      date_after: formatDateToYMD(startDate),
      date_before: formatDateToYMD(endDate),
    };
    if (orderBy) {
      filters.ordering = `${orderDirection === 'desc' ? '-' : ''}${orderBy}`;
    }
    if (Array.isArray(clientStatusFilter) && clientStatusFilter.length > 0) {
      filters.client_status = clientStatusFilter.join(',');
    }
    if (Array.isArray(clientTierFilter) && clientTierFilter.length > 0) {
      filters.client_tier = clientTierFilter.join(',');
    }
    return filters;
  }, [startDate, endDate, orderBy, orderDirection, clientStatusFilter, clientTierFilter]);

  const rollingRanges = useMemo(
    () => ({
      last30: buildTrailingRange(30),
      last60: buildTrailingRange(60),
    }),
    []
  );

  const rollingBaseFilters = useMemo(() => {
    const { date_after, date_before, ...rest } = baseFilters;
    return rest;
  }, [baseFilters]);

  const buildPacingLookup = useCallback((budgets) => {
    const clientMap = new Map();
    const channelMap = new Map();

    const ensureBucket = (map, key) => {
      if (!map.has(key)) {
        map.set(key, {
          budget_amount: 0,
          projected_spend: 0,
          month_to_date_spend: 0,
          today_spend: 0,
          has_today_spend: false,
          today_spend_with_fees: 0,
          has_today_spend_with_fees: false,
          budget_remaining: 0,
          remaining_per_day: 0,
          yesterday_spend: 0,
          yesterday_spend_with_fees: 0,
          has_yesterday_with_fees: false,
          pacing_month_to_date_with_fees: 0,
          has_pacing_month_to_date_with_fees: false,
          month_through_yesterday_with_fees: 0,
          has_month_through_yesterday_with_fees: false,
          actual_month_to_date_with_fees: 0,
          has_actual_month_to_date_with_fees: false,
          verticals: new Map(),
        });
      }
      return map.get(key);
    };

    const addMetrics = (bucket, metrics) => {
      bucket.budget_amount += metrics.amount;
      bucket.projected_spend += metrics.projected_spend;
      bucket.month_to_date_spend += metrics.month_to_date_spend;
      bucket.budget_remaining += metrics.budget_remaining;
      bucket.remaining_per_day += metrics.remaining_per_day;
      bucket.yesterday_spend += metrics.yesterday_spend;
      if (
        typeof metrics.yesterday_spend_with_fees === 'number' &&
        Number.isFinite(metrics.yesterday_spend_with_fees)
      ) {
        bucket.yesterday_spend_with_fees += metrics.yesterday_spend_with_fees;
        bucket.has_yesterday_with_fees = true;
      }
      if (typeof metrics.today_spend === 'number' && Number.isFinite(metrics.today_spend)) {
        bucket.today_spend += metrics.today_spend;
        bucket.has_today_spend = true;
      }
      if (
        typeof metrics.today_spend_with_fees === 'number' &&
        Number.isFinite(metrics.today_spend_with_fees)
      ) {
        bucket.today_spend_with_fees += metrics.today_spend_with_fees;
        bucket.has_today_spend_with_fees = true;
      }
      if (
        typeof metrics.pacing_month_to_date_with_fees === 'number' &&
        Number.isFinite(metrics.pacing_month_to_date_with_fees)
      ) {
        bucket.pacing_month_to_date_with_fees += metrics.pacing_month_to_date_with_fees;
        bucket.has_pacing_month_to_date_with_fees = true;
      }
      if (
        typeof metrics.month_through_yesterday_with_fees === 'number' &&
        Number.isFinite(metrics.month_through_yesterday_with_fees)
      ) {
        bucket.month_through_yesterday_with_fees +=
          metrics.month_through_yesterday_with_fees;
        bucket.has_month_through_yesterday_with_fees = true;
      }
      if (
        typeof metrics.actual_month_to_date_with_fees === 'number' &&
        Number.isFinite(metrics.actual_month_to_date_with_fees)
      ) {
        bucket.actual_month_to_date_with_fees += metrics.actual_month_to_date_with_fees;
        bucket.has_actual_month_to_date_with_fees = true;
      }
    };

    budgets.forEach((budget) => {
      const metrics = {
        amount: numberOrZero(budget.amount),
        projected_spend: numberOrZero(budget.projected_spend),
        month_to_date_spend: numberOrZero(budget.month_to_date_spend),
        budget_remaining: numberOrZero(budget.budget_remaining),
        remaining_per_day: numberOrZero(budget.remaining_per_day),
        yesterday_spend: numberOrZero(
          budget.yesterday_spend ??
            budget?.__pacing_metadata?.yesterday_spend_without_fees ??
            budget?.pacing_metadata?.yesterday_spend_without_fees
        ),
        yesterday_spend_with_fees: toFiniteNumber(
          budget.yesterday_spend_with_fees ??
            budget?.__pacing_metadata?.yesterday_spend_with_fees ??
            budget?.pacing_metadata?.yesterday_spend_with_fees ??
            budget?.__pacing_metadata?.yesterday_spend ??
            budget?.pacing_metadata?.yesterday_spend
        ),
        today_spend: toFiniteNumber(
          budget.today_spend ??
            budget?.__pacing_metadata?.today_spend ??
            budget?.pacing_metadata?.today_spend
        ),
        today_spend_with_fees: toFiniteNumber(
          budget.today_spend_with_fees ??
            budget?.__pacing_metadata?.today_spend_with_fees ??
            budget?.pacing_metadata?.today_spend_with_fees
        ),
        pacing_month_to_date_with_fees: toFiniteNumber(
          budget.pacing_month_to_date_with_fees ??
            budget?.__pacing_metadata?.pacing_month_to_date_with_fees ??
            budget?.pacing_metadata?.pacing_month_to_date_with_fees ??
            budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
            budget?.pacing_metadata?.month_through_yesterday_with_fees
        ),
        month_through_yesterday_with_fees: toFiniteNumber(
          budget.month_through_yesterday_with_fees ??
            budget?.__pacing_metadata?.month_through_yesterday_with_fees ??
            budget?.__pacing_metadata?.actual_through_yesterday_with_fees ??
            budget?.pacing_metadata?.month_through_yesterday_with_fees ??
            budget?.pacing_metadata?.actual_through_yesterday_with_fees
        ),
        actual_month_to_date_with_fees: toFiniteNumber(
          budget.actual_month_to_date_with_fees ??
            budget?.__pacing_metadata?.actual_month_to_date_with_fees ??
            budget?.__pacing_metadata?.spent_with_fees ??
            budget?.pacing_metadata?.actual_month_to_date_with_fees ??
            budget?.pacing_metadata?.spent_with_fees
        ),
      };

      const clientKey = `client-${budget.client}`;
      const clientBucket = ensureBucket(clientMap, clientKey);
      addMetrics(clientBucket, metrics);

      const verticalName = (budget.vertical_data?.vertical || '').trim();
      if (verticalName) {
        const verticalBucket = ensureBucket(clientBucket.verticals, verticalName);
        addMetrics(verticalBucket, metrics);
      }

      if (budget.channel) {
        const channelKey = `client-${budget.client}-channel-${budget.channel}`;
        const channelBucket = ensureBucket(channelMap, channelKey);
        addMetrics(channelBucket, metrics);

        if (verticalName) {
          const channelVerticalBucket = ensureBucket(channelBucket.verticals, verticalName);
          addMetrics(channelVerticalBucket, metrics);
        }
      }
    });

    const finalizeBucket = (bucket) => {
      const amount = bucket.budget_amount;
      const projected = bucket.projected_spend;
      const budgetRemaining = bucket.budget_remaining;
      const remainingPerDay = bucket.remaining_per_day;
      const yesterdaySpend = bucket.has_yesterday_with_fees
        ? bucket.yesterday_spend_with_fees
        : bucket.yesterday_spend;
      const todaySpend = bucket.has_today_spend ? bucket.today_spend : null;
      const todaySpendWithFees = bucket.has_today_spend_with_fees
        ? bucket.today_spend_with_fees
        : null;
      const pacingMonthToDateWithFees = bucket.has_pacing_month_to_date_with_fees
        ? bucket.pacing_month_to_date_with_fees
        : null;
      const monthThroughYesterdayWithFees =
        bucket.has_month_through_yesterday_with_fees
          ? bucket.month_through_yesterday_with_fees
          : null;
      const actualMonthToDateWithFees = bucket.has_actual_month_to_date_with_fees
        ? bucket.actual_month_to_date_with_fees
        : null;
      const daysRemaining = deriveDaysRemaining(budgetRemaining, remainingPerDay);
      const pacingMeta = {};
      if (typeof daysRemaining === 'number' && Number.isFinite(daysRemaining)) {
        pacingMeta.days_remaining = daysRemaining;
      }
      if (typeof yesterdaySpend === 'number' && Number.isFinite(yesterdaySpend)) {
        pacingMeta.yesterday_spend = yesterdaySpend;
        if (bucket.has_yesterday_with_fees) {
          pacingMeta.yesterday_spend_with_fees = yesterdaySpend;
        }
      }
      if (typeof todaySpend === 'number' && Number.isFinite(todaySpend)) {
        pacingMeta.today_spend = todaySpend;
      }
      if (
        typeof todaySpendWithFees === 'number' &&
        Number.isFinite(todaySpendWithFees)
      ) {
        pacingMeta.today_spend_with_fees = todaySpendWithFees;
      }
      if (typeof bucket.month_to_date_spend === 'number' && Number.isFinite(bucket.month_to_date_spend)) {
        pacingMeta.pacing_month_to_date_spend = bucket.month_to_date_spend;
        pacingMeta.month_to_date_spend = bucket.month_to_date_spend;
        if (
          typeof pacingMonthToDateWithFees === 'number' &&
          Number.isFinite(pacingMonthToDateWithFees)
        ) {
          pacingMeta.pacing_month_to_date_with_fees = pacingMonthToDateWithFees;
        }
        const actualThroughYesterdaySpend = Math.max(
          typeof todaySpend === 'number' && Number.isFinite(todaySpend)
            ? bucket.month_to_date_spend - todaySpend
            : bucket.month_to_date_spend,
          bucket.month_to_date_spend,
          0
        );
        if (Number.isFinite(actualThroughYesterdaySpend)) {
          pacingMeta.actual_through_yesterday_spend = actualThroughYesterdaySpend;
        }
      }
      if (
        typeof monthThroughYesterdayWithFees === 'number' &&
        Number.isFinite(monthThroughYesterdayWithFees)
      ) {
        pacingMeta.month_through_yesterday_with_fees = monthThroughYesterdayWithFees;
        pacingMeta.actual_through_yesterday_with_fees = monthThroughYesterdayWithFees;
      } else if (
        typeof pacingMonthToDateWithFees === 'number' &&
        Number.isFinite(pacingMonthToDateWithFees)
      ) {
        pacingMeta.month_through_yesterday_with_fees = pacingMonthToDateWithFees;
      }
      if (
        typeof actualMonthToDateWithFees === 'number' &&
        Number.isFinite(actualMonthToDateWithFees)
      ) {
        pacingMeta.actual_month_to_date_with_fees = actualMonthToDateWithFees;
        pacingMeta.spent_with_fees = actualMonthToDateWithFees;
      }
      const projectedRemainingBudget =
        typeof amount === 'number' && Number.isFinite(amount) &&
        typeof projected === 'number' && Number.isFinite(projected)
          ? amount - projected
          : null;

      const result = {
        budget_amount: roundToTwo(amount),
        projected_spend: roundToTwo(projected),
        month_to_date_spend: roundToTwo(bucket.month_to_date_spend),
        budget_remaining: roundToTwo(budgetRemaining),
        remaining_per_day: roundToTwo(remainingPerDay),
        pacing_percentage: amount > 0 ? roundToTwo((projected / amount) * 100) : 0,
        yesterday_spend: roundToTwo(yesterdaySpend),
      };
      if (
        typeof projectedRemainingBudget === 'number' &&
        Number.isFinite(projectedRemainingBudget)
      ) {
        result.calendar_budget_remaining = roundToTwo(projectedRemainingBudget);
      }
      if (todaySpend !== null) {
        result.today_spend = roundToTwo(todaySpend);
      }
      if (
        typeof todaySpendWithFees === 'number' &&
        Number.isFinite(todaySpendWithFees)
      ) {
        result.today_spend_with_fees = roundToTwo(todaySpendWithFees);
      }
      if (bucket.has_yesterday_with_fees) {
        result.yesterday_spend_with_fees = roundToTwo(
          bucket.yesterday_spend_with_fees
        );
      }
      if (Object.keys(pacingMeta).length > 0) {
        result.__pacing_metadata = pacingMeta;
      }

      if (bucket.verticals.size) {
        result.vertical_budget_breakdown = Array.from(bucket.verticals.entries()).map(
          ([name, vertBucket]) => ({
            vertical: name,
            ...finalizeBucket(vertBucket),
          })
        );
      }

      return result;
    };

    const finalizeMap = (map) => {
      const output = {};
      map.forEach((bucket, key) => {
        output[key] = finalizeBucket(bucket);
      });
      return output;
    };

    return {
      clients: finalizeMap(clientMap),
      channels: finalizeMap(channelMap),
    };
  }, []);

  const mergeMetrics = useCallback(
    (row, metrics) => {
      if (!metrics) {
        return row;
      }

      const merged = { ...row, ...metrics };

      if (metrics.vertical_budget_breakdown) {
        merged.vertical_budget_breakdown = metrics.vertical_budget_breakdown.map((item) => ({
          ...item,
        }));
      }

      return recalculateBudgetMetrics(row, metrics, merged, {
        shouldFallbackToActuals: isCurrentMonthRange,
        calendarToday,
      });
    },
    [isCurrentMonthRange, calendarToday]
  );

  const applyChannelMetrics = useCallback(
    (channels, clientId, lookup) =>
      (Array.isArray(channels) ? channels : []).map((channel) => {
        const key = `client-${clientId}-channel-${channel.channel_id}`;
        const metrics = lookup.channels[key];
        const merged = mergeMetrics({ ...channel }, metrics);
        return withMarketingMetrics(merged);
      }),
    [mergeMetrics]
  );

  const applyClientMetrics = useCallback(
    (clients, lookup) =>
      (Array.isArray(clients) ? clients : []).map((client) => {
        const key = `client-${client.client_id}`;
        const metrics = lookup.clients[key];
        const merged = mergeMetrics({ ...client }, metrics);
        const hydrated = withMarketingMetrics(merged);
        if (hydrated.channels) {
          hydrated.channels = applyChannelMetrics(
            hydrated.channels,
            client.client_id,
            lookup
          );
        }
        return hydrated;
      }),
    [applyChannelMetrics, mergeMetrics]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    const [currentMonthStart] = getCurrentMonthDates();
    const monthSource =
      currentMonthStart instanceof Date
        ? currentMonthStart
        : new Date(currentMonthStart);
    const pacingPayload = {
      month: monthSource.getUTCMonth() + 1,
      year: monthSource.getUTCFullYear(),
    };

    const pacingPromise = fetchBudgetPacing(pacingPayload).catch((error) => {
      console.error('Error fetching pacing data', error);
      return { data: [] };
    });
    const clientPromise = fetchDailyPulseByClient(baseFilters);
    const clientLast30Promise = rollingRanges.last30
      ? fetchDailyPulseByClient({
          ...rollingBaseFilters,
          ...rollingRanges.last30,
        }).catch((error) => {
          console.error('Error fetching last 30 day Daily Pulse data', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const clientLast60Promise = rollingRanges.last60
      ? fetchDailyPulseByClient({
          ...rollingBaseFilters,
          ...rollingRanges.last60,
        }).catch((error) => {
          console.error('Error fetching last 60 day Daily Pulse data', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });

    Promise.all([clientPromise, pacingPromise, clientLast30Promise, clientLast60Promise])
      .then(([pulseResp, pacingResp, last30Resp, last60Resp]) => {
        const budgets = Array.isArray(pacingResp?.data) ? pacingResp.data : [];
        const lookup = buildPacingLookup(budgets);
        setPacingLookup(lookup);

        const clients = Array.isArray(pulseResp?.data) ? pulseResp.data : [];
        const rollingLookup = buildRollingLookups(
          last30Resp?.data,
          last60Resp?.data,
          getClientLookupKey
        );
        const clientsWithRolling = clients.map((client) =>
          attachRollingCosts(client, getClientLookupKey, rollingLookup)
        );
        setTree(applyClientMetrics(clientsWithRolling, lookup));
        setIsError(false);
      })
      .catch((error) => {
        console.error(error);
        setTree([]);
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    isAuthenticated,
    baseFilters,
    rollingBaseFilters,
    rollingRanges,
    startDate,
    buildPacingLookup,
    applyClientMetrics,
  ]);

  useEffect(() => {
    const arr = [];
    tree.forEach((client) => {
      arr.push(client);
      (client.channels || []).forEach((channel) => {
        arr.push(channel);
        (channel.verticals || []).forEach((vertical) => {
          arr.push(vertical);
        });
        (channel.campaigns || []).forEach((campaign) => {
          arr.push(campaign);
        });
      });
    });
    setData(arr);
  }, [tree]);

  const handleClientClick = (row) => {
    const channelPromise = fetchDailyPulseByChannel({
      client_id: row.client_id,
      ...baseFilters,
    });
    const verticalPromise = fetchDailyPulseByVertical({
      client_id: row.client_id,
      ...baseFilters,
    });
    const channelLast30Promise = rollingRanges.last30
      ? fetchDailyPulseByChannel({
          client_id: row.client_id,
          ...rollingBaseFilters,
          ...rollingRanges.last30,
        }).catch((err) => {
          console.error('Error fetching 30 day channel metrics', err);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const channelLast60Promise = rollingRanges.last60
      ? fetchDailyPulseByChannel({
          client_id: row.client_id,
          ...rollingBaseFilters,
          ...rollingRanges.last60,
        }).catch((err) => {
          console.error('Error fetching 60 day channel metrics', err);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const verticalLast30Promise = rollingRanges.last30
      ? fetchDailyPulseByVertical({
          client_id: row.client_id,
          ...rollingBaseFilters,
          ...rollingRanges.last30,
        }).catch((err) => {
          console.error('Error fetching 30 day vertical metrics', err);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const verticalLast60Promise = rollingRanges.last60
      ? fetchDailyPulseByVertical({
          client_id: row.client_id,
          ...rollingBaseFilters,
          ...rollingRanges.last60,
        }).catch((err) => {
          console.error('Error fetching 60 day vertical metrics', err);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });

    Promise.all([
      channelPromise,
      verticalPromise,
      channelLast30Promise,
      channelLast60Promise,
      verticalLast30Promise,
      verticalLast60Promise,
    ])
      .then(([channelResp, verticalResp, channelLast30Resp, channelLast60Resp, verticalLast30Resp, verticalLast60Resp]) => {
        const channels = Array.isArray(channelResp?.data) ? channelResp.data : [];
        const channelRollingLookup = buildRollingLookups(
          channelLast30Resp?.data,
          channelLast60Resp?.data,
          getChannelLookupKey
        );
        const channelsWithMetrics = applyChannelMetrics(
          channels,
          row.client_id,
          pacingLookup
        ).map((channel) => ({
          ...attachRollingCosts(channel, getChannelLookupKey, channelRollingLookup),
          ingestion_health: row.ingestion_health,
          campaigns: [],
          verticals: [],
        }));

        const budgetLookup = new Map(
          (Array.isArray(row.vertical_budget_breakdown)
            ? row.vertical_budget_breakdown
            : []
          ).map((budget) => [
            (budget.vertical || '').trim().toLowerCase(),
            budget,
          ])
        );

        const clientVerticals = (Array.isArray(verticalResp?.data)
          ? verticalResp.data
          : []
        );
        const verticalRollingLookup = buildRollingLookups(
          verticalLast30Resp?.data,
          verticalLast60Resp?.data,
          getVerticalLookupKey
        );
        const clientVerticalsWithRolling = clientVerticals.map((vertical) => {
          const trimmedName = (vertical.vertical || '').trim();
          const key = trimmedName.toLowerCase();
          const budgetMetrics = budgetLookup.get(key) || null;
          const mergedVertical = mergeMetrics(
            {
              ...vertical,
              vertical:
                trimmedName || vertical.vertical || budgetMetrics?.vertical || '',
            },
            budgetMetrics
          );
          const hydratedVertical = withMarketingMetrics(mergedVertical);
          return {
            ...attachRollingCosts(
              hydratedVertical,
              getVerticalLookupKey,
              verticalRollingLookup
            ),
            ingestion_health: row.ingestion_health,
          };
        });

        setTree((prev) =>
          prev.map((item) =>
            item.uuid === row.uuid
              ? {
                  ...item,
                  channels: channelsWithMetrics,
                  verticals: clientVerticalsWithRolling,
                }
              : item
          )
        );
      })
      .catch((err) => {
        console.error('Error fetching client details', err);
      });
  };



  const handleChannelClick = (row) => {
    const client = tree.find((o) => o.client_id === row.client_id);
    const channels = client?.channels || [];
    const channel = channels.find((o) => o.uuid === row.uuid);
    if (!channel) {
      return;
    }
    const hasCampaigns = Array.isArray(channel?.campaigns) && channel.campaigns.length > 0;
    const hasVerticals = Array.isArray(channel?.verticals) && channel.verticals.length > 0;

    if (hasCampaigns || hasVerticals) {
      setTree((prev) =>
        prev.map((item) =>
          item.client_id === row.client_id
            ? {
                ...item,
                channels: (item.channels || []).map((ch) =>
                  ch.uuid === row.uuid
                    ? {
                        ...ch,
                        campaigns: [],
                        verticals: [],
                      }
                    : ch
                ),
              }
            : item
        )
      );
      return;
    }

    const campaignPromise = fetchDailyPulseByCampaign({
      client_id: row.client_id,
      channel_id: row.channel_id,
      ...baseFilters,
    });
    const verticalPromise = fetchDailyPulseByVertical({
      client_id: row.client_id,
      channel_id: row.channel_id,
      ...baseFilters,
    });
    const campaignLast30Promise = rollingRanges.last30
      ? fetchDailyPulseByCampaign({
          client_id: row.client_id,
          channel_id: row.channel_id,
          ...rollingBaseFilters,
          ...rollingRanges.last30,
        }).catch((error) => {
          console.error('Error fetching 30 day campaign metrics', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const campaignLast60Promise = rollingRanges.last60
      ? fetchDailyPulseByCampaign({
          client_id: row.client_id,
          channel_id: row.channel_id,
          ...rollingBaseFilters,
          ...rollingRanges.last60,
        }).catch((error) => {
          console.error('Error fetching 60 day campaign metrics', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const verticalLast30Promise = rollingRanges.last30
      ? fetchDailyPulseByVertical({
          client_id: row.client_id,
          channel_id: row.channel_id,
          ...rollingBaseFilters,
          ...rollingRanges.last30,
        }).catch((error) => {
          console.error('Error fetching 30 day channel vertical metrics', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });
    const verticalLast60Promise = rollingRanges.last60
      ? fetchDailyPulseByVertical({
          client_id: row.client_id,
          channel_id: row.channel_id,
          ...rollingBaseFilters,
          ...rollingRanges.last60,
        }).catch((error) => {
          console.error('Error fetching 60 day channel vertical metrics', error);
          return { data: [] };
        })
      : Promise.resolve({ data: [] });

    Promise.all([
      campaignPromise,
      verticalPromise,
      campaignLast30Promise,
      campaignLast60Promise,
      verticalLast30Promise,
      verticalLast60Promise,
    ])
      .then(([campaignResp, verticalResp, campaignLast30Resp, campaignLast60Resp, verticalLast30Resp, verticalLast60Resp]) => {
        const campaigns = Array.isArray(campaignResp?.data) ? campaignResp.data : [];
        const verticals = Array.isArray(verticalResp?.data) ? verticalResp.data : [];
        const campaignRollingLookup = buildRollingLookups(
          campaignLast30Resp?.data,
          campaignLast60Resp?.data,
          getCampaignLookupKey
        );
        const campaignsWithMetrics = campaigns.map((campaign) => ({
          ...attachRollingCosts(
            withMarketingMetrics(campaign),
            getCampaignLookupKey,
            campaignRollingLookup
          ),
          ingestion_health: channel.ingestion_health || client?.ingestion_health,
        }));

        setTree((prev) =>
          prev.map((item) =>
            item.client_id === row.client_id
              ? {
                  ...item,
                  channels: (item.channels || []).map((ch) => {
                    if (ch.uuid !== row.uuid) {
                      return ch;
                    }

                    const budgetLookup = new Map(
                      (Array.isArray(ch.vertical_budget_breakdown)
                        ? ch.vertical_budget_breakdown
                        : []
                      ).map((budget) => [
                        (budget.vertical || '').trim().toLowerCase(),
                        budget,
                      ])
                    );

                    const verticalRollingLookup = buildRollingLookups(
                      verticalLast30Resp?.data,
                      verticalLast60Resp?.data,
                      getVerticalLookupKey
                    );

                    const mergedVerticals = verticals.map((vertical) => {
                      const trimmedName = (vertical.vertical || '').trim();
                      const key = trimmedName.toLowerCase();
                      const budgetMetrics = budgetLookup.get(key) || null;
                      const mergedVertical = mergeMetrics(
                        {
                          ...vertical,
                          vertical:
                            trimmedName || vertical.vertical || budgetMetrics?.vertical || '',
                        },
                        budgetMetrics
                      );
                      const hydratedVertical = withMarketingMetrics(mergedVertical);
                      return {
                        ...attachRollingCosts(
                          hydratedVertical,
                          getVerticalLookupKey,
                          verticalRollingLookup
                        ),
                        ingestion_health: ch.ingestion_health || client?.ingestion_health,
                      };
                    });

                    return {
                      ...ch,
                      campaigns: campaignsWithMetrics,
                      verticals: mergedVerticals,
                    };
                  }),
                }
              : item
          )
        );
      })
      .catch((error) => {
        console.error('Error fetching channel details', error);
      });
  };

  const loadData = (row) => {
    if (row.client__name) {
      handleClientClick(row);
    } else if (row.channel__name) {
      handleChannelClick(row);
    }
  };

  const setDateRange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const setSort = useCallback((field) => {
    if (field === orderBy) {
      setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(field);
      setOrderDirection('asc');
    }
  }, [orderBy]);

  useEffect(() => {
    setTree((prev) =>
      prev.map((client) => ({
        ...client,
        channels: [],
        verticals: [],
      }))
    );
  }, [startDate, endDate, clientStatusFilter, clientTierFilter]);

  useEffect(() => {
    setTree((prev) =>
      prev.map((client) => ({
        ...client,
        channels: (client.channels || []).map((channel) => ({
          ...channel,
          campaigns: [],
          verticals: [],
        })),
      }))
    );
  }, [orderBy, orderDirection]);

  const value = useMemo(() => ({
    isLoading,
    isError,
    data,
    loadData,
    startDate,
    endDate,
    setDateRange,
    orderBy,
    orderDirection,
    setSort,
    clientStatusFilter,
    setClientStatusFilter,
    clientTierFilter,
    setClientTierFilter,
  }), [
    isLoading,
    isError,
    data,
    loadData,
    startDate,
    endDate,
    setDateRange,
    orderBy,
    orderDirection,
    setSort,
    clientStatusFilter,
    clientTierFilter,
  ]);

  return (
    <DailyPulseContext.Provider value={value}>
      {children}
    </DailyPulseContext.Provider>
  );
};
